import type { AnalyticsListener } from '../types/listener.js';
import type { EventEnvelope } from '../types/envelope.js';

export interface ClarityListenerConfig {
  projectId: string;
}

const CLARITY_SCRIPT_BASE = 'https://www.clarity.ms/tag/';

declare global {
  interface Window {
    clarity?: (cmd: string, ...args: unknown[]) => void;
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function loadClarityScript(projectId: string): Promise<void> {
  return new Promise((resolve) => {
    if (!isBrowser()) {
      resolve();
      return;
    }
    if (typeof window.clarity === 'function') {
      resolve();
      return;
    }
    // Set up the Clarity queue stub BEFORE loading the tag script.
    // The tag script at clarity.ms/tag/{id} expects window.clarity to exist
    // as a queue function so it can process early calls and bootstrap properly.
    // This mirrors the standard Clarity snippet:
    //   window.clarity = window.clarity || function(){ (window.clarity.q = window.clarity.q || []).push(arguments) };
    const w = window as unknown as Record<string, unknown>;
    w['clarity'] = w['clarity'] || function (...args: unknown[]) {
      const fn = w['clarity'] as unknown as { q?: unknown[][] };
      (fn.q = fn.q || []).push(args);
    };
    const script = document.createElement('script');
    script.async = true;
    script.src = `${CLARITY_SCRIPT_BASE}${projectId}`;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

/**
 * Listener that sends events to Microsoft Clarity via script injection.
 * Loads Clarity script with projectId; track → clarity("event", name); identify → clarity("identify", userId, ...).
 * No-op when typeof window === 'undefined' (SSR).
 */
export function createClarityListener(config: ClarityListenerConfig): AnalyticsListener {
  const projectId = config.projectId;
  let ready = false;

  return {
    async init(cfg: unknown): Promise<void> {
      try {
        const c = (cfg ?? config) as ClarityListenerConfig;
        const id = c.projectId;
        if (!id || !isBrowser()) return;
        await loadClarityScript(id);
        ready = true;
      } catch {
        // swallow
      }
    },
    track(envelope: EventEnvelope): void {
      try {
        if (!isBrowser() || !ready || typeof window.clarity !== 'function') return;
        window.clarity('event', envelope.name);
        if (envelope.properties && typeof window.clarity === 'function') {
          for (const [key, value] of Object.entries(envelope.properties)) {
            // clarity('set', key, value) accepts string values.
            // Convert numbers and booleans to strings so they are not silently dropped.
            if (typeof value === 'string') {
              window.clarity('set', key, value);
            } else if (typeof value === 'number' || typeof value === 'boolean') {
              window.clarity('set', key, String(value));
            } else if (Array.isArray(value)) {
              window.clarity('set', key, value);
            }
          }
        }
      } catch {
        // swallow
      }
    },
    identify(userId: string, traits?: Record<string, unknown>): void {
      try {
        if (!isBrowser() || !ready || typeof window.clarity !== 'function') return;

        // PII Safety: Only pass the pseudonymous userId to Clarity's identify.
        // Do NOT forward PII fields (email, name, displayName) to a third-party service.
        // The userId (GUID) can be correlated back to a real user in your own system.
        window.clarity('identify', userId);

        // Forward only non-PII traits as Clarity custom tags.
        // Keys containing PII are stripped to stay compliant with GDPR / CCPA.
        if (traits) {
          const piiKeys = new Set(['email', 'name', 'displayName', 'firstName', 'lastName', 'username', 'phone', 'address']);
          for (const [key, value] of Object.entries(traits)) {
            if (piiKeys.has(key)) continue;
            if (typeof value === 'string') {
              window.clarity('set', key, value);
            } else if (typeof value === 'number' || typeof value === 'boolean') {
              window.clarity('set', key, String(value));
            } else if (Array.isArray(value)) {
              window.clarity('set', key, value);
            }
          }
        }
      } catch {
        // swallow
      }
    },
  };
}
